import { motion } from 'framer-motion';
import { Check, Sparkles, Store, Users } from 'lucide-react';

const plans = [
  {
    name: 'Small Business',
    icon: Store,
    description: 'Perfect for convenience stores and small retailers',
    price: '49',
    priceNote: 'per month',
    transactionFee: '1.5% per transaction',
    features: [
      'Up to 1,000 transactions/month',
      'Single store location',
      'Basic analytics dashboard',
      'Email support',
      'Digital receipt system',
    ],
    cta: 'Start free trial',
    popular: false,
    style: 'bg-white border border-slate-100',
  },
  {
    name: 'Growing Chain',
    icon: Store,
    description: 'For supermarkets and growing retail chains',
    price: '199',
    priceNote: 'per month',
    transactionFee: '1.2% per transaction',
    features: [
      'Unlimited transactions',
      'Up to 10 store locations',
      'Advanced analytics & reports',
      'Priority support',
      'Custom branding',
      'POS integration',
      'Inventory sync',
      'Customer insights',
    ],
    cta: 'Start free trial',
    popular: true,
    style: 'bg-brand-secondary text-white',
  },
  {
    name: 'Enterprise',
    icon: Users,
    description: 'Custom solutions for large retail chains',
    price: 'Custom',
    priceNote: 'contact us',
    transactionFee: 'Volume pricing',
    features: [
      'Everything in Growing Chain',
      'Unlimited store locations',
      'Dedicated account manager',
      'Custom integrations',
      'SLA guarantee',
      'White-label option',
      'On-site training',
      'API access',
    ],
    cta: 'Contact sales',
    popular: false,
    style: 'bg-white border border-slate-100',
  },
];

const Pricing = () => {
  return (
    <section id="pricing" className="py-20 md:py-32 bg-neutral-bg-secondary">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-semibold tracking-wide uppercase text-brand-primary mb-4 block">
            Pricing for Stores
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-brand-secondary mb-6">
            Plans that{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-primary to-brand-accent">
              grow with you
            </span>
          </h2>
          <p className="text-lg md:text-xl font-medium leading-relaxed text-neutral-text-secondary max-w-2xl mx-auto">
            Free for shoppers. Simple pricing for stores. No hidden fees.
          </p>
        </motion.div>

        {/* Shopper Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-gradient-to-r from-brand-primary to-brand-accent rounded-2xl p-6 md:p-8 text-white text-center mb-12"
        >
          <h3 className="font-heading text-2xl font-bold mb-2">Free for Shoppers</h3>
          <p className="text-white/80">Download the app and start scanning. No fees, no subscriptions.</p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-6 items-stretch">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`relative rounded-2xl p-8 lg:p-10 ${plan.style} ${
                plan.popular ? 'shadow-2xl transform md:scale-105 z-10' : 'shadow-[0_2px_8px_rgba(0,0,0,0.04)]'
              } transition-all duration-300 hover:shadow-xl flex flex-col`}
              data-testid={`pricing-card-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <div className="inline-flex items-center gap-1 bg-brand-primary text-white px-4 py-1 rounded-full text-sm font-semibold">
                    <Sparkles className="w-4 h-4" />
                    Most Popular
                  </div>
                </div>
              )}

              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${plan.popular ? 'bg-white/20' : 'bg-brand-primary/10'}`}>
                    <plan.icon className={`w-5 h-5 ${plan.popular ? 'text-white' : 'text-brand-primary'}`} />
                  </div>
                  <h3 className={`font-heading text-2xl font-bold ${plan.popular ? 'text-white' : 'text-brand-secondary'}`}>
                    {plan.name}
                  </h3>
                </div>
                <p className={`text-sm mb-6 ${plan.popular ? 'text-slate-300' : 'text-neutral-text-secondary'}`}>
                  {plan.description}
                </p>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    {plan.price !== 'Custom' && <span className={`text-2xl ${plan.popular ? 'text-slate-300' : 'text-neutral-text-secondary'}`}>$</span>}
                    <span className={`font-heading text-5xl font-black ${plan.popular ? 'text-white' : 'text-brand-secondary'}`}>
                      {plan.price}
                    </span>
                  </div>
                  <p className={`text-sm ${plan.popular ? 'text-slate-300' : 'text-neutral-text-secondary'}`}>
                    {plan.priceNote}
                  </p>
                </div>

                {/* Transaction Fee */}
                <div className={`py-4 px-4 rounded-xl mb-6 ${plan.popular ? 'bg-white/10' : 'bg-neutral-bg-secondary'}`}>
                  <p className={`text-sm ${plan.popular ? 'text-slate-300' : 'text-neutral-text-secondary'}`}>
                    Transaction fee
                  </p>
                  <p className={`font-semibold ${plan.popular ? 'text-white' : 'text-brand-secondary'}`}>
                    {plan.transactionFee}
                  </p>
                </div>

                {/* Features */}
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        plan.popular ? 'bg-functional-success' : 'bg-functional-success/20'
                      }`}>
                        <Check className={`w-3 h-3 ${plan.popular ? 'text-white' : 'text-functional-success'}`} />
                      </div>
                      <span className={`text-sm ${plan.popular ? 'text-slate-200' : 'text-neutral-text-secondary'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA Button */}
              <a
                href="#contact"
                className={`block text-center rounded-full px-8 py-4 font-bold transition-all hover:scale-105 active:scale-95 ${
                  plan.popular
                    ? 'bg-white text-brand-secondary hover:bg-slate-100'
                    : 'bg-brand-primary text-white hover:bg-brand-primary-hover shadow-[0_4px_14px_0_rgba(99,91,255,0.39)]'
                }`}
                data-testid={`pricing-cta-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {plan.cta}
              </a>
            </motion.div>
          ))}
        </div>

        {/* Additional Info */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center text-neutral-text-secondary mt-12"
        >
          All plans include SSL encryption, fraud protection, and 24/7 monitoring.{' '}
          <a href="#faq" className="text-brand-primary hover:underline font-medium">
            View full comparison
          </a>
        </motion.p>
      </div>
    </section>
  );
};

export default Pricing;
